/**
 * @module hooks/useMediaQuery
 * @description
 *   Subscribe to a CSS media query via window.matchMedia.
 *
 *   Compact contract for Genre Fit / View tips:
 *   - COMPACT_MAX_LG = '(max-width: 1023px)' matches Tailwind max-lg
 *   - Initial state is false (desktop-safe) until the effect runs
 */

import { useState, useEffect } from 'react';

/** Aligns with Tailwind `max-lg` / Genre Fit stack vs two-column. */
export const COMPACT_MAX_LG = '(max-width: 1023px)';

/**
 * @param {string} query CSS media query string
 * @returns {boolean} whether the query currently matches
 */
export default function useMediaQuery(query) {
  // Desktop-safe until mount — avoids hydration flash into compact chrome.
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);

    onChange();
    // Safari < 14 used addListener; prefer addEventListener when present.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);

  return matches;
}
